import { useMemo } from "react";
import { DesignV2Scope } from "../../../design-v2";
import { CURRENT_ANALYSIS } from "../api/insightsApi";
import {
  CYCLE_ROLE,
  WEEK_STATUS,
  buildTimelineColumns,
} from "../domain/insightsPresentation";
import {
  InsightsSection,
  NoActiveCycleState,
  NoHistoryState,
  SectionError,
  SectionLoading,
  TimeframeSelector,
} from "../components/InsightsChrome";
import InsightsHeader from "../components/InsightsHeader";
import { WeeklyAdherenceCharts } from "../components/AdherenceParts";
import { MuscleVolumeChart, useMuscleVolumeModel } from "../components/MuscleVolumeParts";
import {
  ExercisePerformanceRow,
  StateDistributionBar,
  StateTiles,
} from "../components/ExercisePerformanceParts";
import {
  HUB_DATA_BUILDING_LINE,
  HUB_HISTORICAL_NOTE,
  formatHistoricalHubLine,
  selectHubRows,
} from "../domain/exercisePerformancePresentation";
import {
  insightsPath,
  useAdherence,
  useCurrentAnalysis,
  useExercisePerformance,
  useMuscleVolume,
  useTimeframeParam,
} from "./useInsightsData";

function currentColumnIndex(columns) {
  return columns.findIndex((column) => column.status === WEEK_STATUS.IN_PROGRESS
    && column.segment !== CYCLE_ROLE.PREVIOUS);
}

function isUnavailable(state) {
  return state.status === "success" && state.data?.available === false;
}

function isEmptyHistory(state, timeframe) {
  return timeframe !== CURRENT_ANALYSIS && state.status === "success" && !(state.data?.weeks || []).length;
}

function HubAdherenceCard({ timeframe, onBrowseHistory }) {
  const state = useAdherence(timeframe);
  const columns = useMemo(() => buildTimelineColumns(state.data), [state.data]);
  const isCurrent = timeframe === CURRENT_ANALYSIS;

  let body;
  if (state.status === "loading") body = <SectionLoading label="Loading adherence" />;
  else if (state.status === "error") body = <SectionError what="adherence" onRetry={state.retry} />;
  else if (isUnavailable(state)) body = <NoActiveCycleState titleAs="h3" onBrowseHistory={onBrowseHistory} />;
  else if (isEmptyHistory(state, timeframe)) body = <NoHistoryState titleAs="h3" timeframe={timeframe} />;
  else {
    body = (
      <WeeklyAdherenceCharts
        columns={columns}
        workoutsAs={isCurrent ? "dots" : "bars"}
        highlightIndex={currentColumnIndex(columns)}
      />
    );
  }

  return (
    <InsightsSection
      title="Adherence"
      to={insightsPath("/progress/adherence", timeframe)}
      linkLabel="Open Adherence"
      testId="hub-adherence"
    >
      {body}
    </InsightsSection>
  );
}

function HubMuscleVolumeCard({ timeframe, onBrowseHistory }) {
  const state = useMuscleVolume(timeframe);
  const model = useMuscleVolumeModel(state.status === "success" ? state.data : null);

  let body;
  if (state.status === "loading") body = <SectionLoading label="Loading muscle volume" />;
  else if (state.status === "error") body = <SectionError what="muscle volume" onRetry={state.retry} />;
  else if (isUnavailable(state)) body = <NoActiveCycleState titleAs="h3" onBrowseHistory={onBrowseHistory} />;
  else if (isEmptyHistory(state, timeframe)) body = <NoHistoryState titleAs="h3" timeframe={timeframe} />;
  else {
    // The chart alone: the muscles, actual and planned are read from its
    // legend; per-week figures live on the Muscle Volume screen.
    body = (
      <MuscleVolumeChart
        columns={model.columns}
        muscles={model.defaultMuscles}
        basis="weightedSets"
        highlightIndex={model.weekIndex}
        hasVolume={model.hasVolume}
        legend="bottom"
      />
    );
  }

  return (
    <InsightsSection
      title="Muscle Volume"
      to={insightsPath("/progress/muscle-volume", timeframe)}
      linkLabel="Open Muscle Volume"
      testId="hub-muscle-volume"
    >
      {body}
    </InsightsSection>
  );
}

/*
 * Exercise Performance card (§6.3). Whether EP is unlocked is known only from
 * an overview response, so until one is held the Hub renders nothing for EP:
 * no section, no skeleton, no error (a placeholder that later disappears
 * would reveal a hidden feature and move the layout). A locked user, and the
 * no-active-cycle case (the whole Hub shows its own state), get no card. The
 * card is the last child of the Hub, so its late mount moves no other card.
 */
function HubExercisePerformanceCard({ timeframe }) {
  const state = useExercisePerformance(timeframe);
  if (state.status !== "success" || !state.data || state.data.available !== true) return null;
  const { data } = state;
  const isCurrent = timeframe === CURRENT_ANALYSIS;

  let subtitle = null;
  let body;
  if (!isCurrent) {
    // OC-1: a factual count only; states belong to Current Analysis.
    body = (
      <div className="lz-insights-ep-lines" data-testid="ep-historical">
        <p className="lz-insights-ep-line">{formatHistoricalHubLine(data.summary?.exerciseCount ?? 0, timeframe)}</p>
        <p className="lz-insights-ep-note">{HUB_HISTORICAL_NOTE}</p>
      </div>
    );
  } else if (data.summary?.dataBuilding) {
    body = <p className="lz-insights-ep-line" data-testid="ep-data-building">{HUB_DATA_BUILDING_LINE}</p>;
  } else {
    subtitle = "Current state distribution";
    const rows = selectHubRows(data.exercises);
    body = (
      <>
        <StateDistributionBar stateCounts={data.summary.stateCounts} />
        <StateTiles stateCounts={data.summary.stateCounts} />
        {rows.length ? (
          <div className="lz-insights-ep-rows">
            {rows.map((row) => <ExercisePerformanceRow key={row.exerciseId} row={row} timeframe={timeframe} />)}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <InsightsSection
      title="Exercise Performance"
      subtitle={subtitle}
      to={insightsPath("/progress/exercise-performance", timeframe)}
      linkLabel="Open Exercise Performance"
      testId="hub-exercise-performance"
    >
      {body}
    </InsightsSection>
  );
}

export default function InsightsHub() {
  const [timeframe, setTimeframe] = useTimeframeParam();
  const analysis = useCurrentAnalysis();
  const isCurrent = timeframe === CURRENT_ANALYSIS;
  const browseHistory = () => setTimeframe("3M");
  // One page-level empty state when Current Analysis itself says there is no
  // active cycle; the cards keep their own handling otherwise.
  const noActiveCycle = isCurrent && isUnavailable(analysis);

  return (
    <DesignV2Scope className="lz-insights" data-testid="insights-hub">
      <InsightsHeader />
      <main className="lz-insights__content">
        <TimeframeSelector value={timeframe} onChange={setTimeframe} />
        {noActiveCycle ? (
          <NoActiveCycleState onBrowseHistory={browseHistory} />
        ) : (
          <>
            <HubAdherenceCard timeframe={timeframe} onBrowseHistory={browseHistory} />
            <HubMuscleVolumeCard timeframe={timeframe} onBrowseHistory={browseHistory} />
            <HubExercisePerformanceCard timeframe={timeframe} />
          </>
        )}
      </main>
    </DesignV2Scope>
  );
}
